/* SingleSignOnComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const Renderer = require("./SingleSignOnRenderer");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";

/**
 * @class SingleSignOnComponent
 * @extends EventEmitter
 *
 * @fires SingleSignOnComponent#Shutdown
 * @fires SingleSignOnComponent#DataUpdated
 * @fires SingleSignOnComponent#DirtyState
 * @fires SingleSignOnComponent#OrganizationDataChange
 */

class SingleSignOnComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userSession,
        logger,
        userFeedback,
        SystemConfig,
        userService,
        uiutils,
        CredentialsEntityDef
    ) {
        super();
        this._OrganizationService = OrganizationService;
        this._userSession = userSession;
        this._logger = logger;
        this._modalUserFeedback = userFeedback;
        this._SystemConfig = SystemConfig;
        this._userService = userService;
        this._uiutils = uiutils;
        this.allOrgs = undefined;
        this._credentialsEntityDef = CredentialsEntityDef;
        this._credentialsList = [];

        this._systemConfigEntity = undefined;
        this._samlConfigBase = undefined;
        this._samlOptions = undefined;

        this._oauthConfigBase = undefined;
        this._oauthOptions = {};

        this._localLoginPageDisabledBase = false;
        this._updatedLocalLoginPageDisabled = false;

        this._isSamlDirty = false;
        this._isoauthDirty = false;
        // Objects to store changes
        this._updatedSamlOptions = undefined;
        this._updatedOauthOptions = undefined;

        this._isLdapRadiusEnabled = false;

        // 0 - disabled
        // 1 - saml
        // 2 - oauth
        this._authSelection = "disabled";
        this._updatedAuthSelection = "disabled";

        this._updatedSamlConfig = {};
        this._updatedOauthConfig = {};

        this._orgId = _.get(userSession, "activeOrganization.id");
        this._isDirty = false;
        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        if (!_.isUndefined(this.activeOrgId)) {
            return Promise.all([
                this.getMyUser(),
                this.getSystemConfig(),
                this._getOrganizations(),
                this._retrieveAllCredentials(),
            ]).then((values) => {
                this._systemConfigEntity = values[1];

                this._samlConfigBase = this._systemConfigEntity.getSamlConfig() || {};
                this._oauthConfigBase = this._systemConfigEntity.getOauthConfig() || {};

                this._localLoginPageDisabledBase = _.get(
                    this._systemConfigEntity.getProperty("localLogin"),
                    "pageDisabled",
                    false
                );
                this._updatedLocalLoginPageDisabled = this._localLoginPageDisabledBase;

                this._updatedSamlConfig = _.cloneDeep(this._samlConfigBase);
                this._updatedOauthConfig = _.cloneDeep(this._oauthConfigBase);
                this._loadSamlConfiguration();
                this._loadOauthConfiguration();

                if (this._samlConfigBase && this._samlConfigBase.enabled) {
                    this._authSelection = "saml";
                    this._updatedAuthSelection = "saml";
                } else if (this._oauthConfigBase && this._oauthConfigBase.enabled) {
                    this._authSelection = "oauth";
                    this._updatedAuthSelection = "oauth";
                } else {
                    this._authSelection = "disabled";
                    this._updatedAuthSelection = "disabled";
                }
                return Promise.resolve();
            });
        } else {
            return Promise.reject();
        }
    }

    _loadSamlConfiguration() {
        return Promise.resolve().then(() => {
            this._updatedSamlConfig.enabled = true;
            this._updateSamlConfigurations();
            return Promise.resolve();
        });
    }

    _loadOauthConfiguration() {
        return Promise.resolve().then(() => {
            this._updatedOauthConfig.enabled = true;
            this._updateoauthConfigurations();
            return Promise.resolve();
        });
    }

    _updateSamlConfigurations() {
        this._samlOptions = this._SystemConfig._cleanSamlOptions(
            this._systemConfigEntity.getSamlOptions()
        );

        if (_.isEmpty(this._samlOptions)) {
            this._samlOptions = this._systemConfigEntity.getBasicSamlOptions();
        }
        if (this._samlOptions.advanced) {
            this._samlOptions = this._SystemConfig.convertBasicSamlOptionsToAdvanced(
                this._samlOptions
            );
        }

        this._updatedSamlOptions = _.cloneDeep(this._samlOptions);
        Object.assign(this._updatedSamlConfig, {options: this._updatedSamlOptions});

        if (_.isEmpty(this._samlConfigBase)) {
            this._samlConfigBase = _.cloneDeep(this._updatedSamlConfig);
        }
    }

    _updateoauthConfigurations() {
        this._oauthOptions = this._SystemConfig._cleanOauthOptions(
            this._systemConfigEntity.getOauthOptions()
        );

        if (_.isEmpty(this._oauthOptions)) {
            this._oauthOptions = this._systemConfigEntity.getBasicOauthOptions();
        }
        if (this._oauthOptions.advanced) {
            this._oauthOptions = this._SystemConfig.convertBasicOauthOptionsToAdvanced(
                this._oauthOptions
            );
        }

        this._updatedOauthOptions = _.cloneDeep(this._oauthOptions);
        Object.assign(this._updatedOauthConfig, {options: this._updatedOauthOptions});

        if (_.isEmpty(this._oauthConfigBase)) {
            this._oauthConfigBase = _.cloneDeep(this._updatedOauthConfig);
        }
    }

    shutdown() {
        /**
         * @event SingleSignOnComponent#Shutdown
         */
        this.emit("Shutdown");
        this._unregisterListeners();
        this.removeAllListeners();
        return Promise.all([this._renderer.shutdown()]).then(
            () => {
                this._systemConfigEntity = undefined;
                this._SystemConfig = undefined;
                this._credentialsEntityDef = undefined;
                this._isDirty = undefined;
                this._uiutils = undefined;

                this._OrganizationService = undefined;
                this._userSession = undefined;
                this._logger = undefined;
                this._modalUserFeedback = undefined;
                this._userService = undefined;
                this._credentialsList = undefined;
                this._samlConfigBase = undefined;
                this._oauthConfigBase = undefined;
                this._samlOptions = undefined;
                this._oauthOptions = undefined;
                this._localLoginPageDisabledBase = undefined;
                this._updatedLocalLoginPageDisabled = undefined;

                this._isSamlDirty = undefined;
                this._isoAuthDirty = undefined;

                this._updatedSamlOptions = undefined;
                this._updatedOauthOptions = undefined;

                this._authSelection = undefined;
                this._updatedAuthSelection = undefined;

                this._updatedSamlConfig = undefined;
                this._updatedOauthConfig = undefined;
                this._orgId = undefined;

                this.allOrgs = undefined;
                this._isLdapRadiusEnabled = undefined;
            },
            (err) => {
                console.log("Error shutting down SingleSignOnComponent: ", err);
            }
        );
    }

    get logger() {
        return this._logger;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    organizationChanged({id, name}) {
        if (this._orgId === id) {
            return;
        }
        this._orgId = id;
        this._triggerOrganization({id, name});
    }

    _triggerDataUpdated() {
        /**
         * @event SingleSignOnComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    _triggerOrganization(org) {
        /**
         * @event SingleSignOnComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit("Organization", org);
    }

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event SingleSignOnComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    getActiveOrgId() {
        return this._orgId;
    }

    get myUserInfo() {
        return this._myUserInfo;
    }

    getSystemConfig() {
        return this._SystemConfig.loadSystemConfig(GSO_ID);
        // return sysDetailsFeed.whenInitialized();
    }

    getMyUser() {
        let userRequest = {
            userId: this._userSession.getUser().getId(),
            descendantOrgActionGroupId: this._orgId,
        };
        return this._userService.get(userRequest).then(
            (myInfo) => {
                this._myUserInfo = myInfo;
                _.set(
                    this.defaultProperties,
                    "planPrefs.catalogCreationAuthGroup",
                    _.keys(myInfo.availableActionGroups)[0]
                );
            },
            (err) => {
                console.log("I am unable to request your user info.", err);
            }
        );
    }

    _getOrganizations() {
        return this._OrganizationService.getOrganizations({}).then(
            (result) => {
                this.allOrgs = result;
                this._isLdapRadiusEnabled =
                    Object.values(this.allOrgs).findIndex(
                        (org) =>
                            (org.radius && !org.radius.inactive) || (org.ldap && !org.ldap.inactive)
                    ) > -1;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "SingleSignOnComponent._getOrganizations() I am unable to request your user/org info."
                );
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getAdvancedOptions(options) {
        return this._SystemConfig.convertBasicSamlOptionsToAdvanced(options);
    }

    _getAdvancedOauthOptions(options) {
        return this._SystemConfig.convertBasicOauthOptionsToAdvanced(options);
    }

    async _setSamlAdvancedOptions(isAdvanced) {
        let wasAdvanced = _.get(this._updatedSamlOptions, "advanced", false);

        await this._retrieveNameForCredential(
            _.get(this._updatedSamlOptions, "decryptionPvk"),
            this._updatedSamlOptions,
            "decryptionPvk"
        );

        if (!wasAdvanced && isAdvanced) {
            this._updatedSamlOptions = this._getAdvancedOptions(this._updatedSamlOptions);
        }

        await this._retrieveNameForCredential(
            _.get(this._updatedSamlOptions, "privateKey"),
            this._updatedSamlOptions,
            "privateKey"
        );

        _.set(this._updatedSamlOptions, "advanced", true);
    }

    async _setOauthAdvancedOptions(isAdvanced) {
        let wasAdvanced = _.get(this._updatedOauthOptions, "advanced", false);

        await this._retrieveNameForCredential(
            _.get(this._updatedOauthOptions, "clientSecret"),
            this._updatedOauthOptions,
            "clientSecret"
        );

        if (!wasAdvanced && isAdvanced) {
            this._updatedOauthOptions = this._getAdvancedOauthOptions(this._updatedOauthOptions);
        }
        _.set(this._updatedOauthOptions, "advanced", true);
    }

    _setSamlBasicOptions(isAdvanced) {
        let wasAdvanced = _.get(this._updatedSamlOptions, "advanced", false);
        if (wasAdvanced && !isAdvanced) {
            this._updatedSamlOptions = this._SystemConfig._cleanSamlOptions(
                this._updatedSamlOptions
            );

            if (_.isEmpty(this._updatedSamlOptions)) {
                this._updatedSamlOptions = this._systemConfigEntity.getBasicSamlOptions();
            }
        }
        _.set(this._updatedSamlOptions, "advanced", false);
    }

    _setOauthBasicOptions(isAdvanced) {
        let wasAdvanced = _.get(this._updatedOauthOptions, "advanced", false);
        if (wasAdvanced && !isAdvanced) {
            this._updatedOauthOptions = this._SystemConfig._cleanOauthOptions(
                this._updatedOauthOptions
            );

            if (_.isEmpty(this._updatedOauthOptions)) {
                this._updatedOauthOptions = this._systemConfigEntity.getBasicOauthOptions();
            }
        }
        _.set(this._updatedOauthOptions, "advanced", false);
    }

    _setSamlConfig() {
        Object.assign(this._updatedSamlConfig, {options: this._updatedSamlOptions});
        this._systemConfigEntity.setSamlConfig(this._updatedSamlConfig);
    }

    _setOauthConfig() {
        Object.assign(this._updatedOauthConfig, {options: this._updatedOauthOptions});
        this._systemConfigEntity.setOauthConfig(this._updatedOauthConfig);
    }

    _setLocalLoginConfig() {
        if (this._updatedAuthSelection === "disabled") {
            this._updatedLocalLoginPageDisabled = false;
        }
        this._systemConfigEntity.setProperty(
            "localLogin",
            Object.assign({}, this._systemConfigEntity.getProperty("localLogin"), {
                pageDisabled: this._updatedLocalLoginPageDisabled,
            })
        );
    }

    _saveConfig() {
        this._setSamlConfig();
        this._setOauthConfig();
        this._setLocalLoginConfig();
        return this._systemConfigEntity
            .update()
            .then((updatedEntity) => {
                this._systemConfigEntity = updatedEntity;
                this._samlConfigBase = this._systemConfigEntity.getSamlConfig() || {};
                this._oauthConfigBase = this._systemConfigEntity.getOauthConfig() || {};
                this._localLoginPageDisabledBase = _.get(
                    this._systemConfigEntity.getProperty("localLogin"),
                    "pageDisabled",
                    false
                );
                this._updatedLocalLoginPageDisabled = this._localLoginPageDisabledBase;
                this._updateSamlConfigurations();
                this._updateoauthConfigurations();
                this._isSamlDirty = false;
                this._authSelection = _.clone(this._updatedAuthSelection);
                return this._samlConfig;
            })
            .catch((err) => {
                this._logger.warn(err);
                this._modalUserFeedback.displayError(err);
            });
    }

    _cancelChanges() {
        try {
            this._updatedAuthSelection = _.cloneDeep(this._authSelection);
            this._updatedSamlOptions = _.cloneDeep(this._samlOptions);
            this._updatedSamlConfig = _.cloneDeep(this._samlConfigBase);

            this._updatedOauthOptions = _.cloneDeep(this._oauthOptions);
            this._updatedOauthConfig = _.cloneDeep(this._oauthConfigBase);

            this._updatedLocalLoginPageDisabled = this._localLoginPageDisabledBase;
        } catch (err) {
            this._logger.warn(err);
        }
    }

    _ssoMechanismChange(value) {
        switch (value) {
            case "saml":
                if (this._updatedSamlConfig) {
                    this._updatedSamlConfig.enabled = true;
                    this._updatedOauthConfig.enabled = false;
                }
                break;
            case "oauth":
                if (this._updatedOauthConfig) {
                    this._updatedSamlConfig.enabled = false;
                    this._updatedOauthConfig.enabled = true;
                }
                break;
            case "disabled":
                if (this._updatedSamlConfig) {
                    this._updatedSamlConfig.enabled = false;
                }
                if (this._updatedOauthConfig) {
                    this._updatedOauthConfig.enabled = false;
                }
                break;
        }
    }

    async _retrieveAllCredentials() {
        this._credentialsList = await this._credentialsEntityDef.loadLabelValues(this._orgId);
        return Promise.resolve();
    }

    _retrieveNameForCredential(val, parent, parentPath) {
        return this._credentialsEntityDef.convertCredIdsToNames(
            this._orgId,
            val,
            parent,
            parentPath
        );
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    _displayError(err) {
        const errorMsg = err.reduce((msg, err) => {
            return `${msg}\n${err.key}: ${err.err}`;
        }, "");
        this._uiutils.writeErrorMessage(`Error: ${errorMsg}`);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = SingleSignOnComponent;

SingleSignOnComponent.$groupName = "Global";

SingleSignOnComponent.$displayName = "Single Sign-On";
SingleSignOnComponent.$license = ["GLUWARE"];

SingleSignOnComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.userSession",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.entities.SystemConfig",
    "$teve.services.instance.User",
    "$teve.ui.UiUtils",
    "$teve.entities.Credentials",
];
